<template>
  <div class="sd-engangsydelser text-dark">
    <h2 class="marg-1-bottom capitalize">Engangsydelser</h2>
    <template v-if="isNoConfiguration">
      <div class="sd-engangsydelser-no-config">
        <h2>
          <i>Skolens Silkeborg Data-integration er ikke konfigureret korrekt. Kontakt Docendo for at få hjælp til
            opsætningen - enten på chat eller email.</i>
        </h2>
      </div>
    </template>
    <template v-else>
      <div class="sd-engangsydelser__header">
        <div class="sd-engangsydelser__header__left">
          <Tabs
            :tabs="viewTabs"
            :selected="viewTab"
            @select="onSelectViewTab">
          </Tabs>
          <template v-if="viewTab === 'employments'">
            <DoFiltersFilter
              :title="'Personer'"
              :tags="personNames"
              :selectedTags="selectedPersonNames"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg personer'"
              @select="togglePersonName"
              @clear="selectedPersonNames = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              :title="'Grupper'"
              :tags="userTags"
              :selectedTags="selectedUserTags"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg grupper'"
              @select="toggleUserTag"
              @clear="selectedUserTags = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              v-if="employmentTitles.length > 1"
              :title="'Ansættelser'"
              :tags="employmentTitles"
              :selectedTags="selectedEmploymentTitles"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg stilling'"
              @select="toggleEmploymentTitle"
              @clear="selectedEmploymentTitles = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              v-if="organisations.length > 1"
              :title="'Organisationer'"
              :tags="organisations"
              :selectedTags="selectedOrganisations"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg organisation'"
              @select="toggleOrganisation"
              @clear="selectedOrganisations = []">
            </DoFiltersFilter>
            <span class="sd-engangsydelser__count">
              Viser {{ filteredRows.length }} af {{ allRows.length }} ansættelser
            </span>
          </template>
          <template v-else>
            <DoDatePickerRange
              :allowSameDate="true"
              :startDate="startDate"
              :endDate="endDate"
              :maximumDateRange="730"
              @startChanged="startDateChanged"
              @endChanged="endDateChanged">
            </DoDatePickerRange>
            <DoFiltersFilter
              :title="'Personer'"
              :tags="uniqueNames"
              :selectedTags="selectedNames"
              :numberOfSelectedTagsToShow="2"
              :placeholder="'Vælg personer'"
              @select="toggleName"
              @clear="selectedNames = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              :title="'Lønart'"
              :tags="uniqueLoenart"
              :selectedTags="selectedLoenart"
              :numberOfSelectedTagsToShow="2"
              :placeholder="'Vælg lønart'"
              @select="toggleLoenart"
              @clear="selectedLoenart = []">
            </DoFiltersFilter>
          </template>
        </div>
        <div class="sd-engangsydelser__header__right" v-if="viewTab !== 'employments'">
          <button class="do-bttn do-bttn--small" :disabled="isLoading" @click="readEngangsydelser">
            Opdater
          </button>
        </div>
      </div>

      <!-- Værktøjslinje: Alle ansættelser (opret) -->
      <div class="sd-engangsydelser__toolbar" v-if="viewTab === 'employments'">
        <span class="sd-engangsydelser__toolbar__wrap"
              :content="selectedEmploymentIds.length === 0 ? 'Vælg personer først' : ''"
              v-tippy="{
                arrow: true,
                placement: 'top',
                animateFill: false,
                animation: 'shift-away',
                boundary: 'window'
              }">
          <button class="do-bttn do-bttn--submit"
                  :class="{'do-bttn--submit--disabled': selectedEmploymentIds.length === 0}"
                  :disabled="selectedEmploymentIds.length === 0"
                  @click="openBulkCreate">
            Opret engangsydelser
          </button>
        </span>
        <button class="do-bttn" v-if="selectedEmploymentIds.length > 0" @click="cancelBatchMode">Annuller valg</button>
        <span class="sd-engangsydelser__count" v-if="selectedEmploymentIds.length > 0">
          {{ selectedEmploymentIds.length }} valgt
        </span>
      </div>

      <!-- Info: Udbakke (sendes automatisk) -->
      <div class="sd-engangsydelser__info" v-if="viewTab === 'outbox'">
        Engangsydelser i udbakken sendes automatisk til SD senere på dagen. Du kan slette dem, så længe de ligger her.
      </div>

      <div class="sd-engangsydelser__body">
        <!-- Tab: Alle ansættelser -->
        <template v-if="viewTab === 'employments'">
          <Loader v-if="isLoadingEmployments"></Loader>
          <table v-else class="do-table">
            <thead>
            <tr>
              <th class="td-check do-table__toggle-all" @click="toggleAllSelected">
                <div class="do-checkbox" :class="{'do-checkbox--selected': isAllFilteredSelected}"></div>
              </th>
              <th>NAVN</th>
              <th>ANSÆTTELSE</th>
              <th>ORGANISATION</th>
              <th class="th-row-actions"></th>
            </tr>
            </thead>
            <tbody v-if="filteredRows.length === 0">
            <tr>
              <td :colspan="5" class="italic">Ingen ansættelser fundet</td>
            </tr>
            </tbody>
            <tbody v-for="group in groupedRows" :key="group.userId" class="user-group">
            <tr v-for="row in group.rows"
                :key="row.id"
                class="employment-row"
                :class="{'is-selected': isRowSelected(row)}"
                @click="onRowClick(row)">
              <td class="td-check" @click.stop="onCheckboxClick(row)">
                <div class="do-checkbox" :class="{'do-checkbox--selected': isRowSelected(row)}"></div>
              </td>
              <td class="bold">{{ row.userName }}</td>
              <td class="td-organisation">
                {{ row.employmentNumber }} - {{ row.employmentTitle }}
              </td>
              <td class="td-organisation">{{ row.organisation }}</td>
              <td class="td-row-actions">
                <button
                  v-if="!isBatchMode"
                  type="button"
                  class="do-bttn do-bttn--small row-action-btn"
                  @click.stop="openSingleCreate(row)"
                >Opret engangsydelse</button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>

        <!-- Tabs: Udbakke / Sendt -->
        <template v-else>
          <Loader v-if="isLoading"></Loader>
          <table v-else class="do-table do-table--striped">
            <thead>
            <tr>
              <th
                class="th-sorter td-created"
                @click="setSort('vCreatedAt')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': !isSortPristine && sortBy==='vCreatedAt'}">
                OPRETTET <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter"
                @click="setSort('name')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='name'}">
                NAVN <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter"
                @click="setSort('employment_number')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='employment_number'}">
                ANSÆTTELSE <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter"
                @click="setSort('loenart')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='loenart'}">
                LØNART <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter td-units"
                @click="setSort('antal')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='antal'}">
                ANTAL <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter td-units"
                @click="setSort('beloeb')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='beloeb'}">
                BELØB <span class="icon-menu-open"></span>
              </th>
              <th>BESKRIVELSE</th>
              <th
                class="th-sorter td-date"
                @click="setSort('dato')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='dato'}">
                DATO <span class="icon-menu-open"></span>
              </th>
              <th class="td-created-by">OPRETTET AF</th>
              <th class="td-status">STATUS</th>
              <th class="th-actions"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="displayedRows.length === 0">
              <td :colspan="11" class="italic">
                {{ emptyTableText }}
              </td>
            </tr>
            <tr v-for="item in displayedRows" :key="item.id" class="engangsydelse-row">
              <td class="td-created">{{ item.vCreatedAt }}</td>
              <td>{{ item.vUserName }}</td>
              <td>{{ item.vEmploymentNumber }}</td>
              <td>{{ item.vLoenart }}</td>
              <td class="td-units">{{ formatDecimal(item.enheder) }}</td>
              <td class="td-units">{{ formatDecimal(item.sats) }}</td>
              <td class="td-description">{{ item.description }}</td>
              <td class="td-date">{{ item.vDato }}</td>
              <td class="td-created-by">{{ item.vCreatedBy }}</td>
              <td class="td-status">
                <span class="status-pill" :class="'status-pill--' + item.state">{{ stateLabel(item.state) }}</span>
              </td>
              <td class="td-actions">
                <button v-if="viewTab === 'outbox' && item.can_delete"
                        class="do-bttn do-bttn--small do-bttn--danger"
                        :disabled="deletingIds.includes(item.id)"
                        @click.stop="deleteRow(item)">
                  Slet
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
    </template>

    <SdCreateEngangsydelse
      v-if="createEmploymentIds.length > 0"
      :schoolId="schoolId"
      :employmentIds="createEmploymentIds"
      :subtitle="createSubtitle"
      @created="onCreated"
      @close="closeCreate">
    </SdCreateEngangsydelse>
  </div>
</template>

<script>
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'
import Loader from '../../common/Loader.vue'
import moment from 'moment'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import DoDatePickerRange from '../../datepicker/DoDatePickerRange.vue'
import SdCreateEngangsydelse from './SdCreateEngangsydelse.vue'
import Tabs from '../../common/Tabs.vue'

export default {
  name: 'SdEngangsydelser',
  components: {Loader, DoFiltersFilter, DoDatePickerRange, SdCreateEngangsydelse, Tabs},
  data () {
    return {
      isNoConfiguration: false,
      isLoading: false,
      engangsydelser: [],
      sortBy: 'vCreatedAt',
      sortDir: 'desc',
      isSortPristine: true,
      selectedNames: [],
      selectedLoenart: [],
      // Engangsydelser kan være bag- og fremdaterede. Indtil brugeren selv
      // ændrer intervallet, synkroniseres det til de datoer der faktisk ligger i listen.
      startDate: moment().subtract(1, 'month').startOf('month').startOf('day'),
      endDate: moment().add(1, 'month').endOf('month').endOf('day'),
      dateFilterTouched: false,
      viewTab: 'employments',
      selectedPersonNames: [],
      selectedUserTags: [],
      selectedEmploymentTitles: [],
      selectedOrganisations: [],
      selectedEmploymentIds: [],
      isBatchMode: false,
      createEmploymentIds: [],
      createSubtitle: '',
      deletingIds: []
    }
  },
  computed: {
    viewTabs () {
      return [
        {id: 'employments', title: 'Oprettelse'},
        {id: 'outbox', title: `Udbakke (${this.outboxCount})`},
        {id: 'sent', title: `Sendt til lønsystem (${this.sentCount})`}
      ]
    },
    outboxCount () {
      return this.engangsydelserSorted.filter(x => x.state !== 'sent').length
    },
    sentCount () {
      return this.engangsydelserSorted.filter(x => x.state === 'sent').length
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    isLoadingEmployments () {
      return this.$store.state.employments && this.$store.state.employments.isLoadingEmploymentsTypesForSchool
    },
    users () {
      return this.$store.getters.usersSortedOnName || []
    },
    usersById () {
      const map = {}
      const list = this.users
      for (let i = 0; i < list.length; i++) {
        map[list[i].id] = list[i].name
      }
      return map
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash || {}
    },
    // Ét row pr. (bruger, SD-ansættelse) med tjenestenummer.
    allRows () {
      const out = []
      for (let i = 0; i < this.users.length; i++) {
        const user = this.users[i]
        const emps = this.employmentsInUserIdHash[user.id] || []
        for (let j = 0; j < emps.length; j++) {
          const emp = emps[j]
          const empNumber = emp && emp.employment_number ? String(emp.employment_number) : ''
          if (empNumber.trim() === '') continue
          if (emp.source === 'opus') continue
          // Kun ansættelser med en stillingskategori kan vælges til engangsydelser.
          if (emp.employment_template_id == null) continue
          const title = emp.source_title || emp.title || 'Ansættelse'
          out.push({
            id: user.id + '_' + emp.id,
            userId: user.id,
            userName: user.name,
            userTags: Array.isArray(user.user_tags) ? user.user_tags : [],
            employmentId: emp.id,
            employmentTitle: title,
            employmentNumber: empNumber,
            organisation: emp.organisation || '',
            employment: emp
          })
        }
      }
      return out.sort((a, b) => a.userName.localeCompare(b.userName, 'da-DK', {sensitivity: 'base'}))
    },
    personNames () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].userName) set[this.allRows[i].userName] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    userTags () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        const t = this.allRows[i].userTags || []
        for (let j = 0; j < t.length; j++) {
          if (t[j]) set[t[j]] = true
        }
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    employmentTitles () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].employmentTitle) set[this.allRows[i].employmentTitle] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    organisations () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].organisation) set[this.allRows[i].organisation] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    filteredRows () {
      return this.allRows.filter(row => {
        if (this.selectedPersonNames.length > 0 && this.selectedPersonNames.indexOf(row.userName) === -1) return false
        if (this.selectedUserTags.length > 0) {
          const match = row.userTags && this.selectedUserTags.some(t => row.userTags.indexOf(t) > -1)
          if (!match) return false
        }
        if (this.selectedEmploymentTitles.length > 0 && this.selectedEmploymentTitles.indexOf(row.employmentTitle) === -1) return false
        if (this.selectedOrganisations.length > 0 && this.selectedOrganisations.indexOf(row.organisation) === -1) return false
        return true
      })
    },
    isAllFilteredSelected () {
      if (this.filteredRows.length === 0) return false
      return this.filteredRows.every(row => this.selectedEmploymentIds.indexOf(row.employmentId) > -1)
    },
    groupedRows () {
      const groups = []
      const indexByUser = {}
      for (let i = 0; i < this.filteredRows.length; i++) {
        const row = this.filteredRows[i]
        if (indexByUser[row.userId] === undefined) {
          indexByUser[row.userId] = groups.length
          groups.push({userId: row.userId, rows: []})
        }
        groups[indexByUser[row.userId]].rows.push(row)
      }
      return groups
    },
    employmentsAll () {
      return this.$store.state.employments && this.$store.state.employments.employments
        ? this.$store.state.employments.employments
        : []
    },
    employmentsById () {
      const map = {}
      const list = this.employmentsAll || []
      for (let i = 0; i < list.length; i++) {
        map[list[i].id] = list[i]
      }
      return map
    },
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    salaryCodesById () {
      const map = {}
      const codes = this.salaryCodes || []
      for (let i = 0; i < codes.length; i++) {
        map[codes[i].id] = codes[i]
      }
      return map
    },
    salaryCodesByLoenart () {
      const map = {}
      const codes = this.salaryCodes || []
      for (let i = 0; i < codes.length; i++) {
        if (codes[i].loenart != null) map[String(parseInt(codes[i].loenart, 10))] = codes[i]
      }
      return map
    },
    engangsydelserSorted () {
      const result = []
      for (let i = 0; i < this.engangsydelser.length; i++) {
        const item = this.engangsydelser[i]
        const employment = item.employment_id ? this.employmentsById[item.employment_id] : null
        const userId = (employment && employment.user_id) || item.user_id
        const vUserName = (userId && this.usersById[userId]) ||
          (item.tjenestenummer ? String(item.tjenestenummer) : 'Ukendt')
        const vEmploymentNumber = employment && employment.employment_number
          ? employment.employment_number + (employment.title ? ' - ' + employment.title : '')
          : (item.tjenestenummer ? String(item.tjenestenummer) : 'Ukendt')
        const salaryCode = (item.salary_code_id && this.salaryCodesById[item.salary_code_id]) ||
          (item.loenart != null ? this.salaryCodesByLoenart[String(parseInt(item.loenart, 10))] : null)
        const loenartLabel = item.loenart != null ? String(item.loenart) : ''
        const vLoenart = salaryCode && salaryCode.name
          ? (salaryCode.loenart || loenartLabel) + ' - ' + salaryCode.name
          : loenartLabel
        result.push(Object.assign({}, item, {
          vUserName,
          vEmploymentNumber,
          vLoenart,
          loenartLabel,
          vCreatedAt: item.created_at ? moment(item.created_at).format('lll') : '',
          vCreatedBy: this.createdByName(item.approved_by),
          vDato: this.formatActivityDate(item),
          can_delete: item.can_delete != null ? item.can_delete : (item.state === 'draft')
        }))
      }
      return result.sort((a, b) => {
        const av = a.created_at ? moment(a.created_at).valueOf() : 0
        const bv = b.created_at ? moment(b.created_at).valueOf() : 0
        return bv - av
      })
    },
    uniqueNames () {
      const set = {}
      for (let i = 0; i < this.engangsydelserSorted.length; i++) {
        const name = this.engangsydelserSorted[i].vUserName || ''
        if (name) set[name] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    uniqueLoenart () {
      const set = {}
      for (let i = 0; i < this.engangsydelserSorted.length; i++) {
        const l = this.engangsydelserSorted[i].loenartLabel || ''
        if (l) set[l] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    // Rækker inden for det valgte datointerval (aktivitetsdato). Rækker uden dato
    // beholdes, så ingen poster gemmes væk.
    dateFilteredRows () {
      if (!this.startDate || !this.endDate) return this.engangsydelserSorted
      const from = moment(this.startDate).startOf('day')
      const to = moment(this.endDate).endOf('day')
      return this.engangsydelserSorted.filter(x => {
        if (!x.vDato) return true
        const dato = moment(x.vDato, 'DD-MM-YYYY')
        return dato.isSameOrAfter(from) && dato.isSameOrBefore(to)
      })
    },
    emptyTableText () {
      if (this.hasActiveListFilters) return 'Ingen engangsydelser matcher filtrene'
      return this.viewTab === 'outbox' ? 'Udbakken er tom' : 'Ingen afsendte engangsydelser'
    },
    hasActiveListFilters () {
      return this.selectedNames.length > 0 || this.selectedLoenart.length > 0
    },
    displayedRows () {
      let rows = this.dateFilteredRows.slice()
      rows = rows.filter(x => this.viewTab === 'sent' ? x.state === 'sent' : x.state !== 'sent')
      if (this.selectedNames.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedNames.length; i++) chosen[this.selectedNames[i]] = true
        rows = rows.filter(x => chosen[x.vUserName || ''])
      }
      if (this.selectedLoenart.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedLoenart.length; i++) chosen[this.selectedLoenart[i]] = true
        rows = rows.filter(x => chosen[x.loenartLabel || ''])
      }

      const get = (item, key) => {
        switch (key) {
          case 'name':
            return item.vUserName || ''
          case 'employment_number':
            return String(item.vEmploymentNumber || '')
          case 'loenart':
            return String(item.loenartLabel || item.vLoenart || '')
          case 'dato':
            return item.vDato ? moment(item.vDato, 'DD-MM-YYYY').toDate() : new Date(0)
          case 'antal':
            return this._num(item.enheder)
          case 'beloeb':
            return this._num(item.sats)
          case 'vCreatedAt':
            return item.created_at ? moment(item.created_at).valueOf() : 0
          default:
            return item[key] || ''
        }
      }

      const dir = this.sortDir === 'asc' ? 1 : -1
      const key = this.sortBy

      return rows.slice().sort((a, b) => {
        const va = get(a, key)
        const vb = get(b, key)
        if (va === vb) return 0
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        if (va instanceof Date && vb instanceof Date) return (va - vb) * dir
        if (typeof va === 'number' && typeof vb !== 'number') return -1 * dir
        if (typeof vb === 'number' && typeof va !== 'number') return 1 * dir
        return String(va).localeCompare(String(vb), 'da', {sensitivity: 'base'}) * dir
      })
    }
  },
  methods: {
    // Slår navnet op på den bruger (approved_by) der oprettede ydelsen.
    // Findes brugeren ikke i skolens brugerliste, vises selve id'et.
    createdByName (approvedBy) {
      if (approvedBy == null) return ''
      return this.usersById[approvedBy] || String(approvedBy)
    },
    onSelectViewTab (tab) {
      this.viewTab = tab
      if (tab !== 'employments' && !this.isLoading) {
        this.readEngangsydelser()
      }
    },
    togglePersonName (name) {
      const idx = this.selectedPersonNames.indexOf(name)
      if (idx > -1) this.selectedPersonNames.splice(idx, 1)
      else this.selectedPersonNames.push(name)
    },
    toggleUserTag (tag) {
      const idx = this.selectedUserTags.indexOf(tag)
      if (idx > -1) this.selectedUserTags.splice(idx, 1)
      else this.selectedUserTags.push(tag)
    },
    toggleEmploymentTitle (title) {
      const idx = this.selectedEmploymentTitles.indexOf(title)
      if (idx > -1) this.selectedEmploymentTitles.splice(idx, 1)
      else this.selectedEmploymentTitles.push(title)
    },
    toggleOrganisation (organisation) {
      const idx = this.selectedOrganisations.indexOf(organisation)
      if (idx > -1) this.selectedOrganisations.splice(idx, 1)
      else this.selectedOrganisations.push(organisation)
    },
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx > -1) this.selectedNames.splice(idx, 1)
      else this.selectedNames.push(name)
    },
    toggleLoenart (loenart) {
      const idx = this.selectedLoenart.indexOf(loenart)
      if (idx > -1) this.selectedLoenart.splice(idx, 1)
      else this.selectedLoenart.push(loenart)
    },
    setSort (key) {
      this.isSortPristine = false
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortDir = 'asc'
      }
    },
    startDateChanged (date) {
      this.dateFilterTouched = true
      this.startDate = date
    },
    endDateChanged (date) {
      this.dateFilterTouched = true
      this.endDate = date
    },
    syncDateRangeFromData () {
      if (this.dateFilterTouched) return
      let min = null
      let max = null
      for (let i = 0; i < this.engangsydelser.length; i++) {
        const item = this.engangsydelser[i]
        if (item.aar == null || item.maaned == null || item.dag == null) continue
        const year = 2000 + parseInt(item.aar, 10)
        const d = moment({
          year: year,
          month: parseInt(item.maaned, 10) - 1,
          day: parseInt(item.dag, 10)
        })
        if (!d.isValid()) continue
        if (!min || d.isBefore(min)) min = d.clone()
        if (!max || d.isAfter(max)) max = d.clone()
      }
      if (min && max) {
        this.startDate = min.startOf('day')
        this.endDate = max.endOf('day')
      }
    },
    _num (v) {
      if (v == null || v === '') return 0
      const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'))
      return Number.isFinite(n) ? n : 0
    },
    isRowSelected (row) {
      return this.selectedEmploymentIds.indexOf(row.employmentId) > -1
    },
    toggleSelection (row) {
      const idx = this.selectedEmploymentIds.indexOf(row.employmentId)
      if (idx > -1) this.selectedEmploymentIds.splice(idx, 1)
      else this.selectedEmploymentIds.push(row.employmentId)
    },
    toggleAllSelected () {
      this.isBatchMode = true
      const visibleIds = this.filteredRows.map(row => row.employmentId)
      if (this.isAllFilteredSelected) {
        this.selectedEmploymentIds = this.selectedEmploymentIds.filter(id => visibleIds.indexOf(id) === -1)
      } else {
        const set = {}
        for (let i = 0; i < this.selectedEmploymentIds.length; i++) set[this.selectedEmploymentIds[i]] = true
        for (let i = 0; i < visibleIds.length; i++) set[visibleIds[i]] = true
        this.selectedEmploymentIds = Object.keys(set).map(id => parseInt(id, 10))
      }
    },
    cancelBatchMode () {
      this.isBatchMode = false
      this.selectedEmploymentIds = []
    },
    onRowClick (row) {
      if (this.isBatchMode) this.toggleSelection(row)
      else this.openSingleCreate(row)
    },
    onCheckboxClick (row) {
      if (!this.isBatchMode) this.isBatchMode = true
      this.toggleSelection(row)
    },
    openSingleCreate (row) {
      this.createEmploymentIds = [row.employmentId]
      this.createSubtitle = row.userName + ' – ' + row.employmentNumber + ' - ' + row.employmentTitle
    },
    openBulkCreate () {
      if (this.selectedEmploymentIds.length === 0) return
      this.createEmploymentIds = this.selectedEmploymentIds.slice()
      this.createSubtitle = ''
    },
    closeCreate () {
      this.createEmploymentIds = []
      this.createSubtitle = ''
    },
    onCreated () {
      this.closeCreate()
      this.cancelBatchMode()
      this.viewTab = 'outbox'
      this.readEngangsydelser()
    },
    deleteRow (item) {
      if (item.id == null || this.deletingIds.includes(item.id)) return
      this.deletingIds.push(item.id)
      docendo.deleteSilkeborgDataEngangsydelse(this.schoolId, item.id, 'ydelse', () => {
        this.deletingIds = this.deletingIds.filter(id => id !== item.id)
        this.engangsydelser = this.engangsydelser.filter(r => r.id !== item.id)
        eventbus.toast = {text: 'Engangsydelsen blev slettet fra udbakken', success: true, position: 'bottomLeft'}
      }, failure => {
        console.log('delete failure', failure)
        this.deletingIds = this.deletingIds.filter(id => id !== item.id)
        eventbus.toast = {text: 'Kunne ikke slette engangsydelsen', success: false, position: 'bottomLeft'}
      })
    },
    formatActivityDate (item) {
      if (item.aar == null || item.maaned == null || item.dag == null) return ''
      const year = 2000 + parseInt(item.aar, 10)
      return moment({year: year, month: parseInt(item.maaned, 10) - 1, day: parseInt(item.dag, 10)}).format('DD-MM-YYYY')
    },
    formatDecimal (value) {
      if (value == null || value === '') return ''
      const num = Number(value)
      if (isNaN(num)) return String(value).replace('.', ',')
      const rounded = Math.round(num * 100) / 100
      return String(rounded).replace('.', ',')
    },
    stateLabel (state) {
      switch (state) {
        case 'draft': return 'I udbakke'
        case 'sending': return 'Sender'
        case 'sent': return 'Sendt'
        default: return state || ''
      }
    },
    readEngangsydelser () {
      this.isLoading = true
      docendo.listSilkeborgDataEngangsydelser(this.schoolId, 'ydelse', success => {
        if (success && success.status && success.status === 'error') {
          this.isNoConfiguration = true
        }
        if (Array.isArray(success)) {
          this.engangsydelser = success
        } else if (success && Array.isArray(success.data)) {
          this.engangsydelser = success.data
        } else {
          this.engangsydelser = []
        }
        this.syncDateRangeFromData()
        this.isLoading = false
      }, failure => {
        console.log(failure)
        this.isLoading = false
        this.engangsydelser = []
        if (failure && failure.status && failure.status === 'error') {
          this.isNoConfiguration = true
        }
      })
    }
  },
  watch: {
    selectedEmploymentIds (ids) {
      if (!ids || ids.length === 0) this.isBatchMode = false
    }
  },
  mounted () {
    this.$store.dispatch('getUsersFromSchoolId', this.schoolId)
    this.$store.dispatch('readEmployments')
    this.$store.dispatch('readSalaryCodes')
    this.readEngangsydelser()
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.sd-engangsydelser {
  padding: 1rem;
}

.sd-engangsydelser__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.sd-engangsydelser__header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.sd-engangsydelser__header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-left: auto;
}

.sd-engangsydelser__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.sd-engangsydelser__toolbar__wrap {
  display: inline-flex;
}

.sd-engangsydelser__count {
  font-style: italic;
  font-size: 11px;
  color: #6b7280;
}

.sd-engangsydelser__info {
  font-size: 12px;
  font-style: italic;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.th-sorter {
  cursor: pointer;
  user-select: none;
}

.th-sorter:hover {
  transition: 0.15s;
}

.th-sorter:active {
  transform: scale(0.98);
}

.th-sorter.th-sorter--active {
  color: var(--primary, #468ad7);
}

.do-table tbody td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.do-table tbody tr {
  transition: background-color 0.15s;
}

.do-table tbody tr:hover {
  background-color: aliceblue;
}

.do-table tbody.user-group:nth-of-type(2n) {
  background-color: #f9fafb;
}

.do-table tbody.user-group tr {
  cursor: pointer;
}

.do-table--striped tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.td-check {
  width: 26px;
}

.do-table thead th.td-check {
  border-right: 1px solid #e2e8f0;
}

.do-table tbody td.td-check {
  vertical-align: middle;
}

.do-table__toggle-all {
  color: var(--primary, #468ad7);
  cursor: pointer;
  user-select: none;
  text-transform: none;
  white-space: nowrap;
}

.do-table__toggle-all:hover {
  text-decoration: underline;
}

.do-table tbody tr.is-selected {
  background-color: #eaf3fb;
}

.do-checkbox {
  border: 1px solid #ccc;
  border-radius: .25rem;
  height: 1rem;
  position: relative;
  width: 1rem;
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
}

.do-checkbox.do-checkbox--selected {
  background: var(--primary);
  border-color: var(--primary);
}

.do-checkbox.do-checkbox--selected:after,
.do-checkbox.do-checkbox--selected:before {
  background: #fff;
}

.do-checkbox:after,
.do-checkbox:before {
  background: transparent;
  transform: rotate(35deg);
}

.do-checkbox:after {
  height: 8px;
  right: 3px;
  top: 1px;
  width: 1.5px;
}

.do-checkbox:after,
.do-checkbox:before {
  border-radius: 1rem;
  content: "";
  display: block;
  position: absolute;
}

.do-checkbox.do-checkbox--selected:before {
  bottom: 2.5px;
  height: 1.5px;
  right: 5px;
  width: 4px;
}

.td-organisation {
  opacity: 0.7;
  font-size: 11px;
}

.th-row-actions {
  width: 170px;
}

.td-row-actions {
  width: 170px;
  text-align: right;
  position: relative;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.row-action-btn {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transition: opacity 0.12s ease;
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.employment-row:hover .row-action-btn {
  opacity: 1;
  pointer-events: auto;
}

.td-date, .td-units {
  width: 100px;
}

.td-units {
  text-align: right;
  width: 80px;
}

.do-table thead th.td-units {
  text-align: right;
}

.td-created {
  width: 150px;
}

.td-created-by {
  width: 140px;
}

.td-description {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.capitalize {
  text-transform: capitalize;
}

.td-status {
  width: 110px;
}

.th-actions, .td-actions {
  width: 90px;
  text-align: right;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  line-height: 1.5;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pill--draft {
  background-color: #f3f4f6;
  color: #6b7280;
}

.status-pill--sending {
  background-color: #fef9c3;
  color: #854d0e;
}

.status-pill--sent {
  background-color: #dcfce7;
  color: #166534;
}
</style>



// WEBPACK FOOTER //
// SdEngangsydelser.vue?6506cad9